/**
 * The storefront notice above the menu (ORDR-118; FR-STO-023, 024, 025,
 * FR-QTE-019).
 *
 * A NOTICE, NEVER A REPLACEMENT. It renders in the customer outlet's `notice`
 * slot and the framework's menu stays mounted in every state: a closed
 * location, or one past its cutoff, is still browsable. Only placement is
 * refused, by the guard in `customerServices.ts`.
 *
 * A CLOSED location is drawn as `Closed.dc.html` (ORDR-164): a "Not taking
 * orders right now" heading over the same sentence, and the way back to the
 * chooser reads "Choose an open location". The menu below is unchanged.
 *
 * The boundary shown is `last_order_at`, never `closes_at`. `reason` is an open
 * set: an unknown string takes the generic closed words.
 */

import type { ReactElement } from "react";
import { formatInstant } from "./format.js";
import { PublishedHours } from "./PublishedHours.js";
import type { Hours, Readiness, Windows } from "./storefrontApi.js";
import type { Storefront } from "./useStorefront.js";

const NOTICE = "m-3 rounded-[var(--border-radius-md)] bg-[var(--color-neutral-100)] p-3 text-sm";
const MAX_WINDOWS = 5;
const NOT_TAKING = "Not taking new orders right now.";

export interface StorefrontStatusProps {
  readonly storefront: Storefront;
  /** The flow's mode key, or `null` while the MODE step is still REQUIRED. */
  readonly mode: string | null;
  /** The chooser for this brand, when the address named one; `null` otherwise. */
  readonly changeLocationHref: string | null;
}

function stateSentence(hours: Hours): string {
  const at = (iso: string): string => formatInstant(iso, hours.timezone);
  if (hours.accepts_orders) {
    if (hours.last_order_at === null) {
      return "Open.";
    }
    // A zero cutoff is expressible and ordinary: equal instants are not an error.
    return hours.last_order_at === hours.closes_at
      ? `Open. Orders until ${at(hours.last_order_at)}.`
      : `Open. Last orders at ${at(hours.last_order_at)}.`;
  }
  if (hours.reason === "CAPACITY_ZERO") {
    return NOT_TAKING;
  }
  if (hours.is_open) {
    return hours.last_order_at === null
      ? "Not taking orders right now. You can still browse the menu."
      : `Last orders were at ${at(hours.last_order_at)}. You can still browse the menu.`;
  }
  const closed = hours.next_opens_at === null ? "Closed for orders." : `Closed for orders. Opens ${at(hours.next_opens_at)}.`;
  const explained =
    (hours.reason === "SPECIAL_CLOSURE" || hours.reason === "EMERGENCY_CLOSURE") &&
    hours.closure_reason !== null &&
    hours.closure_reason.trim() !== "";
  return explained ? `${closed} ${hours.closure_reason}` : closed;
}

function HoursState({ hours }: { hours: Storefront["hours"] }): ReactElement | null {
  if (hours === null) {
    return null;
  }
  if (hours.kind === "ok") {
    return (
      <p role="status" data-testid="storefront-state">
        {stateSentence(hours.hours)}
      </p>
    );
  }
  return (
    <p role="alert" data-testid="storefront-state">
      We could not check whether this location is taking orders. You can still browse the menu.
      {hours.kind === "failed" && ` Reference: ${hours.correlationId}`}
    </p>
  );
}

function ReadinessLine({ readiness }: { readiness: Readiness }): ReactElement | null {
  if (readiness.state === "QUOTED" && readiness.ready_in_seconds !== null) {
    const minutes = Math.max(1, Math.ceil(readiness.ready_in_seconds / 60));
    return (
      <p data-testid="storefront-readiness">
        Estimated ready time at this location: about {minutes} min (an estimate for this location, not a quote for
        your order).
      </p>
    );
  }
  if (readiness.state === "REFUSED" && readiness.reason === "CAPACITY_ZERO") {
    return <p data-testid="storefront-readiness">{NOT_TAKING}</p>;
  }
  // NOT_QUOTED, or anything unknown: the estimate is an aid, and its absence
  // must not read as closed.
  return null;
}

function WindowList({ windows, timeZone }: { windows: Windows; timeZone: string }): ReactElement {
  if (windows.windows.length === 0) {
    return (
      <p data-testid="storefront-windows">
        {windows.refusal?.reason === "NO_WINDOW_IN_HORIZON" ? "No upcoming times are available." : "No upcoming times are listed."}
      </p>
    );
  }
  const soonest = [...windows.windows]
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))
    .slice(0, MAX_WINDOWS);
  return (
    <ul data-testid="storefront-windows">
      {soonest.map((window) => (
        <li key={window.window_id} data-testid="storefront-window">
          {`${window.label ?? window.shift_key ?? "Window"}: ${formatInstant(window.starts_at, timeZone)} to ${formatInstant(window.ends_at, timeZone)}`}
          {window.capacity_limited && window.remaining_units !== null && `, ${window.remaining_units} left`}
        </li>
      ))}
    </ul>
  );
}

export function StorefrontStatus({ storefront, mode, changeLocationHref }: StorefrontStatusProps): ReactElement {
  const { hours, schedule, windows, readiness } = storefront;
  const closed = hours?.kind === "ok" && !hours.hours.is_open && !hours.hours.accepts_orders;
  return (
    <section data-testid="storefront" aria-label="This location" className={NOTICE}>
      {closed && <h2 className="m-0 text-[17px] font-bold">Not taking orders right now</h2>}
      <HoursState hours={hours} />
      {readiness?.kind === "ok" && <ReadinessLine readiness={readiness.readiness} />}
      {windows?.kind === "ok" && hours?.kind === "ok" && <WindowList windows={windows.windows} timeZone={hours.hours.timezone} />}
      {windows !== null && windows.kind !== "ok" && (
        <p role="alert" data-testid="storefront-windows">
          We could not load the upcoming times.{windows.kind === "failed" && ` Reference: ${windows.correlationId}`}
        </p>
      )}
      {schedule?.kind === "ok" && <PublishedHours schedule={schedule.schedule} mode={mode} />}
      {schedule !== null && schedule.kind !== "ok" && (
        <p role="alert" data-testid="storefront-published-hours">
          We could not load this location's hours.{schedule.kind === "failed" && ` Reference: ${schedule.correlationId}`}
        </p>
      )}
      {changeLocationHref !== null && (
        <a href={changeLocationHref} className="mt-2 inline-flex min-h-11 items-center font-semibold underline">
          {closed ? "Choose an open location" : "Change location"}
        </a>
      )}
    </section>
  );
}
